import React, { useState } from 'react';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import { X, Loader2, Mic, ChevronDown } from 'lucide-react';

/**
 * Lightweight "Add Speaker" modal for the speaker_bureau paradigm.
 * Intentionally minimal — only a name is required. Advanced fields
 * (address, travel preferences) live behind a "More details" accordion.
 *
 * Props:
 *  - onClose(): close without saving
 *  - onCreated(speaker): called with the created speaker
 *  - initial (optional): prefill values
 *  - speakerId (optional): when set, edits an existing speaker (PUT)
 */
const HONORIFICS = ['', 'Dr.', 'Prof.', 'Mr.', 'Mrs.', 'Ms.', 'Mx.'];

const AddSpeakerForm = ({ onClose, onCreated, initial = {}, speakerId = null }) => {
  const [form, setForm] = useState({
    honorific: initial.honorific || '',
    full_name: initial.full_name || `${initial.first_name || ''} ${initial.last_name || ''}`.trim(),
    credentials: initial.credentials || '',
    email: initial.email || '',
    phone: initial.phone || '',
    specialty: initial.specialty || '',
    therapeutic_area: initial.therapeutic_area || '',
    affiliation: initial.affiliation || '',
    npi: initial.npi || '',
    default_fee: initial.default_fee || '',
    tags: (initial.tags || []).join(', '),
    notes: initial.notes || '',
  });
  const [showMore, setShowMore] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.full_name.trim()) {
      setError('Please enter the speaker name.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        // Don't send legacy split parts; full_name is the source of truth
        first_name: undefined,
        last_name: undefined,
        tags: form.tags ? form.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
      };
      // Drop empty strings / undefined so we don't store blanks
      Object.keys(payload).forEach((k) => {
        if (payload[k] === '' || payload[k] === undefined) delete payload[k];
      });
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const res = speakerId
        ? await axios.put(`${apiBase}/speakers/${speakerId}`, payload, { headers })
        : await axios.post(`${apiBase}/speakers`, payload, { headers });
      onCreated && onCreated(res.data);
      onClose && onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save speaker.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-rose-500 text-white">
              <Mic className="h-5 w-5" />
            </div>
            <h2 className="text-lg font-semibold text-slate-800">
              {speakerId ? 'Edit Speaker' : 'Add Speaker'}
            </h2>
          </div>
          <button onClick={onClose} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 px-6 py-5">
          {error && (
            <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
          )}

          <div className="grid grid-cols-4 gap-3">
            <div className="col-span-1">
              <label className="mb-1 block text-xs font-semibold text-slate-600">Title</label>
              <select
                value={form.honorific}
                onChange={(e) => set('honorific', e.target.value)}
                className="h-10 w-full rounded-lg border border-slate-200 px-2 text-sm"
              >
                {HONORIFICS.map((h) => (
                  <option key={h} value={h}>{h || '—'}</option>
                ))}
              </select>
            </div>
            <div className="col-span-3">
              <label className="mb-1 block text-xs font-semibold text-slate-600">Credentials</label>
              <input
                value={form.credentials}
                onChange={(e) => set('credentials', e.target.value)}
                placeholder="MD, PhD"
                className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm"
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-600">Name *</label>
            <input
              value={form.full_name}
              onChange={(e) => set('full_name', e.target.value)}
              placeholder="e.g. Dr. Timothy Lynch"
              className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
                className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600">Phone</label>
              <input
                value={form.phone}
                onChange={(e) => set('phone', e.target.value)}
                className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600">Specialty</label>
              <input
                value={form.specialty}
                onChange={(e) => set('specialty', e.target.value)}
                placeholder="e.g. Neurology"
                className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600">Affiliation</label>
              <input
                value={form.affiliation}
                onChange={(e) => set('affiliation', e.target.value)}
                placeholder="Institution / practice"
                className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm"
              />
            </div>
          </div>

          <button
            type="button"
            onClick={() => setShowMore((s) => !s)}
            className="flex items-center gap-1 text-sm font-medium text-rose-600"
          >
            <ChevronDown className={`h-4 w-4 transition-transform ${showMore ? 'rotate-180' : ''}`} />
            More details
          </button>

          {showMore && (
            <div className="space-y-4 border-t border-slate-100 pt-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-600">Therapeutic area</label>
                  <input
                    value={form.therapeutic_area}
                    onChange={(e) => set('therapeutic_area', e.target.value)}
                    className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-600">NPI</label>
                  <input
                    value={form.npi}
                    onChange={(e) => set('npi', e.target.value)}
                    className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-600">Default fee</label>
                  <input
                    value={form.default_fee}
                    onChange={(e) => set('default_fee', e.target.value)}
                    placeholder="e.g. Per Contracted Rate"
                    className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-600">Tags (comma-separated)</label>
                  <input
                    value={form.tags}
                    onChange={(e) => set('tags', e.target.value)}
                    className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">Notes</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => set('notes', e.target.value)}
                  rows={3}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                />
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 rounded-lg bg-rose-600 px-5 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {speakerId ? 'Save' : 'Add Speaker'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddSpeakerForm;
